import { create } from "zustand";

import type { OpenAPI } from "simplydo/interfaces";
import type { MessageThreadWithParticipants } from "simplydo/interfaces/types/messageTypes";

import type { NewMessageThread } from "components/lib/Messaging/types";

type MessageThread = OpenAPI.Schemas["MessageThread"];

export type SelectedMessageThread =
  | MessageThreadWithParticipants
  | MessageThread
  | NewMessageThread
  | string
  | { _id: string; participantUsers?: OpenAPI.Schemas["User"][] };

export const selectedThreadId = (thread: SelectedMessageThread): string =>
  typeof thread === "string" ? thread : thread._id;

export type MessagesState = { selectedMessageThreads: SelectedMessageThread[] };

type MessagesStore = MessagesState & {
  selectThread: (
    thread: SelectedMessageThread | null,
    isPhone?: boolean,
  ) => void;
  closeMessageThread: (threadId: string) => void;
  onThreadCreated: (newThreadId: string, thread: MessageThread) => void;
};

export const initialMessagesState: MessagesState = {
  selectedMessageThreads: [],
};

export const useMessagesStore = create<MessagesStore>((set) => ({
  ...initialMessagesState,
  selectThread: (thread, isPhone = false) =>
    set((state) => {
      if (thread === null) return { selectedMessageThreads: [] };
      if (isPhone) {
        const phoneThread: SelectedMessageThread =
          typeof thread === "string" ? { _id: thread } : thread;
        return { selectedMessageThreads: [phoneThread] };
      }
      const resolved =
        typeof thread === "string"
          ? (state.selectedMessageThreads.find(
              (t) => selectedThreadId(t) === thread,
            ) ?? { _id: thread })
          : thread;
      const resolvedThread = resolved as MessageThread;
      const { selectedMessageThreads } = state;
      const threadIndex = selectedMessageThreads.findIndex(
        (t) => selectedThreadId(t) === selectedThreadId(resolvedThread),
      );
      if (threadIndex > -1) {
        return {
          selectedMessageThreads: selectedMessageThreads.map((t, index) => {
            if (index !== threadIndex) return t;
            return typeof t === "string"
              ? resolvedThread
              : { ...t, ...resolvedThread };
          }),
        };
      }
      if (selectedMessageThreads.length > 2) {
        return {
          selectedMessageThreads: [
            resolvedThread,
            ...selectedMessageThreads.slice(0, 2),
          ],
        };
      }
      return {
        selectedMessageThreads: [resolvedThread, ...selectedMessageThreads],
      };
    }),
  closeMessageThread: (threadId) =>
    set((state) => ({
      selectedMessageThreads: state.selectedMessageThreads.filter(
        (t) => selectedThreadId(t) !== threadId,
      ),
    })),
  onThreadCreated: (newThreadId, thread) =>
    set((state) => ({
      selectedMessageThreads: state.selectedMessageThreads.map((t) =>
        selectedThreadId(t) === newThreadId ? thread : t,
      ),
    })),
}));
